import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../../api/api";
import usePortalChat from '../../../hooks/usePortalChat';
import {
  Container,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Skeleton,
  Alert
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import ClientChat from "./ClientChat";
import ClientEvidence from "./ClientEvidence";
import ClientRentalHistory from "./ClientRentalHistory";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Discount as DiscountIcon,
  ChatBubbleOutline as ChatIcon
} from '@mui/icons-material';

function ClientDetail() {
  const { id } = useParams();
  const { openChat, opening: chatOpening, error: chatError } = usePortalChat(id);
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const requestVersion = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Используем useCallback для объявления функции fetchClient
  const fetchClient = useCallback(async () => {
    const version = ++requestVersion.current;
    setError(null);
    try {
      setLoading(true);
      const response = await api.get(`/clients/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (version !== requestVersion.current) return;
      setClient(response.data && typeof response.data === "object" && !Array.isArray(response.data) ? response.data : null);
    } catch (error) {
      if (version !== requestVersion.current) return;
      console.error("Ошибка при получении клиента:", error);
      enqueueSnackbar("Не удалось загрузить клиента", { variant: "error" });
      setError("Ошибка при загрузке данных клиента");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  // Функция для обновления данных клиента
  const updateClient = useCallback(async () => {
    try {
      setLoading(true);
      await api.put(`/clients/${id}`, client, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      enqueueSnackbar("Данные клиента успешно обновлены", {
        variant: "success",
      });
    } catch (error) {
      console.error("Ошибка при обновлении данных клиента:", error);
      enqueueSnackbar("Не удалось обновить данные клиента", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, client, enqueueSnackbar]);

  // Используем useEffect для загрузки клиента при монтировании компонента
  useEffect(() => {
    fetchClient();
    return () => { requestVersion.current += 1; };
  }, [fetchClient]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? 'Не указано' : date.toLocaleDateString('ru-RU');
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '0 ₽';
    if (!Number.isFinite(Number(amount))) return 'Не указано';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount);
  };

  const getClientName = () => {
    if (!client) return '';
    
    // Сначала пробуем использовать display_name из API
    if (client.display_name) return client.display_name;
    
    if (client.client_type === 'individual') {
      const parts = [client.last_name, client.first_name, client.middle_name].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : client.name || 'Без имени';
    }
    return client.name || 'Без названия';
  };

  const getContactType = (contact) => {
    if (String(contact || '').includes('@')) return 'email';
    return 'phone';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="rectangular" width={100} height={40} />
          <Skeleton variant="text" width={300} height={40} />
        </Box>
        <Grid container spacing={3}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/clients')}
          variant="outlined"
        >
          Назад к списку клиентов
        </Button>
      </Box>
    );
  }

  if (!client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Клиент не найден
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/clients')}
          variant="outlined"
        >
          Назад к списку клиентов
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ClientEvidence key={client.id} clientId={client.id} />
      {/* Хедер с навигацией */}
      <Box className="erp-section-heading" sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/clients')}
            variant="outlined"
          >
            Назад
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: client.client_type === 'individual' ? 'primary.main' : 'secondary.main' }}>
              {client.client_type === 'individual' ? <PersonIcon /> : <BusinessIcon />}
            </Avatar>
            <Box>
              <Typography variant="h4">
                {getClientName()}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {client.client_type === 'individual' ? 'Физическое лицо' : 'Юридическое лицо'} • ID: {client.id}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {!client.is_deleted && <Button variant="outlined" startIcon={<ChatIcon />}
            disabled={chatOpening} onClick={() => openChat({ clientId: id })}>
            {chatOpening ? 'Открываем чат…' : 'Чат с клиентом'}
          </Button>}
          <Button
            startIcon={<EditIcon />}
            onClick={() => navigate(`/clients/edit/${id}`)}
            variant="contained"
          >
            Редактировать
          </Button>
        </Stack>
      </Box>

      {chatError && <Alert severity="error" sx={{ mb: 2 }}>{chatError}</Alert>}

      <Grid container spacing={3}>
        {/* Основная информация */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Основная информация
              </Typography>
              <Stack spacing={2}>
                {client.client_type === 'individual' ? (
                  <>
                    <Box>
                      <Typography variant="body2" color="text.secondary">ФИО</Typography>
                      <Typography variant="body1">
                        {[client.last_name, client.first_name, client.middle_name].filter(Boolean).join(' ') || client.name || 'Не указано'}
                      </Typography>
                    </Box>
                    {client.birth_date && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Дата рождения</Typography>
                        <Typography variant="body1">
                          {formatDate(client.birth_date)}
                        </Typography>
                      </Box>
                    )}
                    {client.gender && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Пол</Typography>
                        <Typography variant="body1">
                          {client.gender === 'male' ? 'Мужской' : client.gender === 'female' ? 'Женский' : 'Не указан'}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Название организации</Typography>
                    <Typography variant="body1">
                      {client.name || 'Не указано'}
                    </Typography>
                  </Box>
                )}
                
                {client.source && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Источник привлечения</Typography>
                    <Chip label={client.source} size="small" variant="outlined" />
                  </Box>
                )}
                
                {client.comment && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Комментарий</Typography>
                    <Typography variant="body1">
                      {client.comment}
                    </Typography>
                  </Box>
                )}
                
                <Box>
                  <Typography variant="body2" color="text.secondary">Дата создания</Typography>
                  <Typography variant="body1">
                    {formatDate(client.created_at)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Контактная информация */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Контактная информация
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getContactType(client.contact_info) === 'email' ? <EmailIcon /> : <PhoneIcon />}
                  <Box>
                    <Typography variant="body2" color="text.secondary">Основной контакт</Typography>
                    <Typography variant="body1">{client.contact_info}</Typography>
                  </Box>
                </Box>
                
                {client.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Дополнительный телефон</Typography>
                      <Typography variant="body1">{client.phone}</Typography>
                    </Box>
                  </Box>
                )}
                
                {client.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Адрес</Typography>
                      <Typography variant="body1">{client.address}</Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Паспортные данные (только для физических лиц) */}
        {client.client_type === 'individual' && (
          client.passport_series || client.passport_number || client.passport_issued_by || client.inn
        ) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon />
                  Паспортные данные
                </Typography>
                <Stack spacing={2}>
                  {(client.passport_series || client.passport_number) && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Серия и номер</Typography>
                      <Typography variant="body1">
                        {[client.passport_series, client.passport_number].filter(Boolean).join(' ') || 'Не указано'}
                      </Typography>
                    </Box>
                  )}
                  
                  {client.passport_issued_by && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Кем выдан</Typography>
                      <Typography variant="body1">{client.passport_issued_by}</Typography>
                    </Box>
                  )}
                  
                  {client.passport_issued_date && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Дата выдачи</Typography>
                      <Typography variant="body1">{formatDate(client.passport_issued_date)}</Typography>
                    </Box>
                  )}
                  
                  {client.passport_division_code && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Код подразделения</Typography>
                      <Typography variant="body1">{client.passport_division_code}</Typography>
                    </Box>
                  )}
                  
                  {client.inn && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">ИНН</Typography>
                      <Typography variant="body1">{client.inn}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Финансовая информация */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon />
                Финансовая информация
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Сумма долга</Typography>
                  <Typography 
                    variant="h6" 
                    color={client.debt > 0 ? 'error.main' : 'success.main'}
                  >
                    {formatCurrency(client.debt)}
                  </Typography>
                </Box>
                
                {client.discount_percent > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiscountIcon color="success" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Скидка</Typography>
                      <Typography variant="body1" color="success.main">
                        {client.discount_percent}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Статусы и настройки */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Статусы и настройки
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {client.black_list && (
                  <Chip 
                    label="В черном списке" 
                    color="error" 
                    icon={<PersonIcon />}
                  />
                )}
                
                {client.no_contract && (
                  <Chip 
                    label="Без договора" 
                    color="warning" 
                    variant="outlined"
                  />
                )}
                
                {client.no_deposit && (
                  <Chip 
                    label="Без залога" 
                    color="info" 
                    variant="outlined"
                  />
                )}
                
                {client.sms_notifications ? (
                  <Chip 
                    label="SMS уведомления включены" 
                    color="success" 
                    variant="outlined"
                  />
                ) : (
                  <Chip 
                    label="SMS уведомления отключены" 
                    color="default" 
                    variant="outlined"
                  />
                )}
                
                <Chip 
                  label={`Статус ассистента: ${Number(client.assistant_status) === 1 ? 'Активен' : 'Неактивен'}`}
                  color={Number(client.assistant_status) === 1 ? 'success' : 'default'}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* История аренд */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                История аренд
              </Typography>
              <ClientRentalHistory clientId={client.id} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ClientDetail;
